import type { Firestore } from 'firebase-admin/firestore'
import { FieldValue } from 'firebase-admin/firestore'
import { getAuth } from 'firebase-admin/auth'
import type { ShareLink, Comment, Plan, Stage } from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'

const logger = createLogger('SharingRepository')

// Remove undefined values from object (Firestore rejects undefined)
function cleanUndefined<T extends Record<string, unknown>>(obj: T): T {
  const result = { ...obj }
  Object.keys(result).forEach(key => {
    if (result[key] === undefined) {
      delete result[key]
    }
  })
  return result
}

export class SharingRepository {
  constructor(private readonly firestore: Firestore) {}

  async createShare(share: Omit<ShareLink, 'id'>): Promise<string> {
    logger.debug('Creating share link', { planId: share.planId })
    const cleanedShare = cleanUndefined(share as Record<string, unknown>)
    const docRef = await this.firestore.collection('shareLinks').add(cleanedShare)
    logger.info('Share link created', { shareId: docRef.id, planId: share.planId })
    return docRef.id
  }

  async getSharesByPlan(planId: string): Promise<ShareLink[]> {
    logger.debug('Fetching shares for plan', { planId })
    const snapshot = await this.firestore
      .collection('shareLinks')
      .where('planId', '==', planId)
      .orderBy('createdAt', 'desc')
      .get()

    logger.info('Shares fetched', { planId, count: snapshot.docs.length })
    return snapshot.docs.map((doc: FirestoreDoc) => ({
      id: doc.id,
      ...this.convertTimestamps(doc.data())
    })) as ShareLink[]
  }

  async findByToken(token: string): Promise<ShareLink | null> {
    logger.debug('Fetching share by token')
    const snapshot = await this.firestore
      .collection('shareLinks')
      .where('token', '==', token)
      .limit(1)
      .get()

    if (snapshot.empty) {
      logger.warn('Share token not found')
      return null
    }

    const doc = snapshot.docs[0] as FirestoreDoc
    logger.info('Share found by token', { shareId: doc.id })
    return { id: doc.id, ...this.convertTimestamps(doc.data()) } as ShareLink
  }

  async getShareById(shareId: string): Promise<ShareLink | null> {
    logger.debug('Fetching share by ID', { shareId })
    const doc = await this.firestore.collection('shareLinks').doc(shareId).get()
    if (!doc.exists) {
      logger.warn('Share not found', { shareId })
      return null
    }
    const data = doc.data() || {}
    logger.info('Share fetched', { shareId })
    return { id: doc.id, ...this.convertTimestamps(data as Record<string, any>) } as ShareLink
  }

  async deleteShare(shareId: string): Promise<void> {
    logger.debug('Deleting share link', { shareId })
    await this.firestore.collection('shareLinks').doc(shareId).delete()
    logger.info('Share link deleted', { shareId })
  }

  async getLatestShare(planId: string): Promise<ShareLink | null> {
    const snapshot = await this.firestore
      .collection('shareLinks')
      .where('planId', '==', planId)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .get()

    if (snapshot.empty) return null
    const doc = snapshot.docs[0] as FirestoreDoc
    return { id: doc.id, ...this.convertTimestamps(doc.data()) } as ShareLink
  }

  async updateShare(shareId: string, updates: Partial<ShareLink>): Promise<ShareLink> {
    const updatePayload = cleanUndefined({
      ...updates,
      updatedAt: updates.updatedAt ?? new Date()
    } as Record<string, unknown>)

    await this.firestore.collection('shareLinks').doc(shareId).update(updatePayload)
    const refreshed = await this.getShareById(shareId)
    if (!refreshed) {
      throw new Error('Share not found after update')
    }
    return refreshed
  }

  async incrementViewCount(shareId: string): Promise<void> {
    logger.debug('Incrementing view count', { shareId })
    await this.firestore.collection('shareLinks').doc(shareId).update({
      viewCount: FieldValue.increment(1)
    })
    logger.debug('View count incremented', { shareId })
  }

  async getComments(planId: string): Promise<Comment[]> {
    logger.debug('Fetching comments for plan', { planId })
    const snapshot = await this.firestore
      .collection('comments')
      .where('planId', '==', planId)
      .where('parentId', '==', null)
      .orderBy('createdAt', 'desc')
      .get()

    // Fixed: Properly await all reply fetches in parallel
    const comments = await Promise.all(
      snapshot.docs.map(async (doc: FirestoreDoc) => {
        const comment = { id: doc.id, ...doc.data() } as Comment
        comment.replies = await this.getReplies(doc.id)
        return comment
      })
    )

    logger.info('Comments fetched', { planId, count: comments.length })
    return comments
  }

  private async getReplies(parentId: string): Promise<Comment[]> {
    logger.debug('Fetching replies for comment', { parentId })
    const snapshot = await this.firestore
      .collection('comments')
      .where('parentId', '==', parentId)
      .orderBy('createdAt', 'asc')
      .get()

    logger.debug('Replies fetched', { parentId, count: snapshot.docs.length })
    return snapshot.docs.map((doc: FirestoreDoc) => {
      const data = doc.data()
      return {
        id: doc.id,
        planId: data.planId,
        userId: data.userId,
        userName: data.userName,
        userAvatar: data.userAvatar,
        content: data.content,
        parentId: data.parentId,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        replies: [] // Max 2 levels (no replies to replies)
      } as Comment
    })
  }

  async createComment(comment: Omit<Comment, 'id'>): Promise<string> {
    logger.debug('Creating comment', { planId: comment.planId, parentId: comment.parentId || null })
    const docRef = await this.firestore.collection('comments').add(comment)
    logger.info('Comment created', { commentId: docRef.id, planId: comment.planId })
    return docRef.id
  }

  async getCommentById(commentId: string): Promise<Comment | null> {
    logger.debug('Fetching comment by ID', { commentId })
    const doc = await this.firestore.collection('comments').doc(commentId).get()
    if (!doc.exists) {
      logger.warn('Comment not found', { commentId })
      return null
    }
    logger.info('Comment fetched', { commentId })
    return { id: doc.id, ...doc.data() } as Comment
  }

  async deleteComment(commentId: string): Promise<void> {
    logger.debug('Deleting comment', { commentId })
    // Delete all replies first
    const repliesSnapshot = await this.firestore
      .collection('comments')
      .where('parentId', '==', commentId)
      .get()

    const batch = createBatchBuilder(this.firestore)
    for (const reply of repliesSnapshot.docs as FirestoreDoc[]) {
      batch.deleteRef(reply.ref)
    }

    // Delete the comment itself
    batch.addDelete('comments', commentId)

    await batch.commit()
    logger.info('Comment deleted with replies', { commentId, repliesCount: repliesSnapshot.docs.length })
  }

  async getPlanById(planId: string): Promise<Plan | null> {
    logger.debug('Fetching plan by ID', { planId })
    const doc = await this.firestore.collection('plans').doc(planId).get()
    if (!doc.exists) {
      logger.warn('Plan not found', { planId })
      return null
    }
    const data = doc.data()
    if (!data) {
      logger.warn('Plan data empty', { planId })
      return null
    }
    logger.info('Plan fetched', { planId })
    return { id: doc.id, ...this.convertTimestamps(data) } as Plan
  }

  async updatePlan(planId: string, updates: Partial<Plan>): Promise<void> {
    logger.debug('Updating plan', { planId, updates: Object.keys(updates) })
    const cleanedUpdates = cleanUndefined({
      ...updates,
      updatedAt: new Date()
    } as Record<string, unknown>)
    await this.firestore.collection('plans').doc(planId).update(cleanedUpdates)
    logger.info('Plan updated', { planId })
  }

  async getPlanStages(planId: string): Promise<Stage[]> {
    logger.debug('Fetching stages for plan', { planId })
    const snapshot = await this.firestore
      .collection('plans')
      .doc(planId)
      .collection('stages')
      .orderBy('order', 'asc')
      .get()

    logger.info('Stages fetched', { planId, count: snapshot.docs.length })
    return snapshot.docs.map((doc: FirestoreDoc) => ({
      id: doc.id,
      ...this.convertTimestamps(doc.data())
    })) as Stage[]
  }

  // Default icon options - MUST match ProfileIconId and ProfileIconColor from @my-roadtrip/types
  private static readonly VALID_ICONS = [
    'hiking', 'explore', 'terrain', 'flight_takeoff', 'bus',
    'directions_car', 'two_wheeler', 'pedal_bike', 'backpack',
    'camera_alt', 'landscape', 'beach_access', 'forest',
    'snowflake', 'waves', 'volunteer_activism', 'group', 'favorite'
  ] as const

  private static readonly VALID_COLORS = [
    'blue', 'purple', 'pink', 'green', 'orange', 'red', 'teal', 'amber', 'cyan'
  ] as const

  private generateDefaultIconSet() {
    const randomIcon = SharingRepository.VALID_ICONS[Math.floor(Math.random() * SharingRepository.VALID_ICONS.length)]
    const randomColor = SharingRepository.VALID_COLORS[Math.floor(Math.random() * SharingRepository.VALID_COLORS.length)]
    return { activeIcon: randomIcon, accentColor: randomColor }
  }

  private generateDisplayNameFromEmail(email: string): string {
    const prefix = email.split('@')[0]
    return prefix
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim()
  }

  async getUserProfile(userId: string): Promise<{ 
    displayName?: string | null
    email?: string | null
    iconSet?: { activeIcon: string; accentColor?: string } | null 
  } | null> {
    logger.debug('Fetching user profile', { userId })
    
    // Try Firestore first (has iconSet)
    const doc = await this.firestore.collection('users').doc(userId).get()
    if (doc.exists) {
      const rawData = doc.data() || {}
      
      // Try multiple possible field names for display name and email
      let displayName = rawData.displayName || rawData.name || rawData.userName || rawData.fullName || null
      let email = rawData.email || rawData.userEmail || rawData.emailAddress || null
      let iconSet = rawData.iconSet || null
      
      // AUTO-FIX: If fields are missing, try to get from Firebase Auth
      const updates: Record<string, unknown> = {}
      
      // 🆕 If email is missing in Firestore, try to get from Firebase Auth
      if (!email) {
        try {
          const authUser = await getAuth().getUser(userId)
          if (authUser.email) {
            email = authUser.email
            updates.email = email
            logger.info('📧 Email recovered from Firebase Auth', { userId, email })
          }
          // Also recover displayName if missing
          if (!displayName && authUser.displayName) {
            displayName = authUser.displayName
            updates.displayName = displayName
          }
        } catch (authErr) {
          logger.debug('Could not fetch from Firebase Auth', { userId, error: (authErr as Error).message })
        }
      }
      
      if (!iconSet) {
        iconSet = this.generateDefaultIconSet()
        updates.iconSet = iconSet
        logger.debug('Auto-generating iconSet for user', { userId })
      }
      
      if (!displayName && email) {
        displayName = this.generateDisplayNameFromEmail(email)
        updates.displayName = displayName
      }
      
      if (!displayName) {
        displayName = 'Voyageur'
        updates.displayName = displayName
      }
      
      // Apply updates in background (don't block the response)
      if (Object.keys(updates).length > 0) {
        updates.updatedAt = new Date()
        void this.firestore.collection('users').doc(userId).update(updates).catch(err => {
          logger.warn('Failed to auto-update user profile', { userId, error: (err as Error).message })
        })
      }
      
      return { displayName, email, iconSet }
    }
    
    // Fallback to Firebase Auth (no iconSet available)
    try {
      const authUser = await getAuth().getUser(userId)
      logger.info('User profile fetched from Firebase Auth', { userId })
      
      // Generate defaults
      const iconSet = this.generateDefaultIconSet()
      const displayName = authUser.displayName || 
        (authUser.email ? this.generateDisplayNameFromEmail(authUser.email) : 'Voyageur')
      
      // 🆕 Create user document in Firestore if it doesn't exist
      const newUserDoc = {
        id: userId,
        uid: userId,
        email: authUser.email || null,
        displayName,
        iconSet,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      void this.firestore.collection('users').doc(userId).set(newUserDoc, { merge: true }).catch(err => {
        logger.warn('Failed to create user document from Auth', { userId, error: (err as Error).message })
      })
      
      return { displayName, email: authUser.email || null, iconSet }
    } catch (authError) {
      logger.warn('User profile not found in Firestore or Auth', { userId, error: (authError as Error).message })
      return null
    }
  }

  private convertTimestamps<T extends Record<string, any>>(data: T): T {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(data)) {
      if (value && typeof value === 'object') {
        if (typeof value.toDate === 'function') {
          result[key] = value.toDate()
        } else if (Array.isArray(value)) {
          result[key] = value.map(item =>
            item && typeof item === 'object' ? this.convertTimestamps(item) : item
          )
        } else {
          result[key] = this.convertTimestamps(value as Record<string, any>)
        }
      } else {
        result[key] = value
      }
    }
    return result as T
  }
}
